import React, { useState, useEffect, Fragment } from "react";
import { Card, Tabs, Alert, Space, Tag, Typography, Button, message } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { Sourcing, SourcingStatus, TechnicalEvaluationMode, EvaluationPhase } from "../interface";
import { PriceTabulation } from "./PriceTabulation";
import { EvaluationMatrix } from "./EvaluationMatrix";
import { ComplianceTabulation } from "./ComplianceTabulation";
import { useCustomMutation } from "@refinedev/core";
import { START_FINANCIAL_EVALUATION_MUTATION, COMPLETE_FINANCIAL_EVALUATION_MUTATION, START_TECHNICAL_EVALUATION_MUTATION, COMPLETE_TECHNICAL_EVALUATION_MUTATION } from "../queries";

const { Text } = Typography;

// Status order for index-based logic
const SOURCING_STATUS_ORDER = [
  SourcingStatus.DRAFT,
  SourcingStatus.SUBMITTED,
  SourcingStatus.APPROVED,
  SourcingStatus.PUBLISHED,
  SourcingStatus.CLOSED,
  SourcingStatus.REJECTED,
  SourcingStatus.RESULT_SUBMITTED,
  SourcingStatus.AWARD_APPROVED,
  SourcingStatus.AWARDED,
];

interface EvaluationTabProps {
  record: Sourcing;
}

export const EvaluationTab: React.FC<EvaluationTabProps> = ({ record }) => {
  const [activeTab, setActiveTab] = useState("technical");
  const [technicalPhase, setTechnicalPhase] = useState<EvaluationPhase>(record.evaluationPhase);
  const [financialPhase, setFinancialPhase] = useState<EvaluationPhase>(record.evaluationPhase);
  const [isTechnicalInProgress, setIsTechnicalInProgress] = useState(false);
  const [isTechnicalCompleted, setIsTechnicalCompleted] = useState(false);
  const [isFinancialInProgress, setIsFinancialInProgress] = useState(false);
  const [isFinancialCompleted, setIsFinancialCompleted] = useState(false);

  const { mutate: startFinancialEvaluation } = useCustomMutation();
  const { mutate: completeFinancialEvaluation } = useCustomMutation();
  const { mutate: startTechnicalEvaluation } = useCustomMutation();
  const { mutate: completeTechnicalEvaluation } = useCustomMutation();

  // Calculate status index for simplified logic
  const statusIndex = SOURCING_STATUS_ORDER.indexOf(record.status);

  // Show evaluation only when status is CLOSED
  if (statusIndex !== SOURCING_STATUS_ORDER.indexOf(SourcingStatus.CLOSED)) {
    return (
      <Card>
        <Alert
          message="Evaluation Not Available"
          description="Evaluation data will be available once the sourcing event is closed and bids have been received."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  // Update phase states based on current evaluation phase
  useEffect(() => {
    const phase = record.evaluationPhase;
    setTechnicalPhase(phase);
    setFinancialPhase(phase);
    
    setIsTechnicalInProgress(phase === EvaluationPhase.TECHNICAL_IN_PROGRESS);
    setIsTechnicalCompleted(
      phase === EvaluationPhase.TECHNICAL_COMPLETED ||
      phase === EvaluationPhase.FINANCIAL_IN_PROGRESS ||
      phase === EvaluationPhase.FINANCIAL_COMPLETED ||
      phase === EvaluationPhase.COMPLETE
    );
    
    setIsFinancialInProgress(phase === EvaluationPhase.FINANCIAL_IN_PROGRESS);
    setIsFinancialCompleted(
      phase === EvaluationPhase.FINANCIAL_COMPLETED ||
      phase === EvaluationPhase.COMPLETE
    );
  }, [record.evaluationPhase]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const handleStartFinancialEvaluation = () => {
    startFinancialEvaluation(
      {
        url: "",
        method: "post",
        values: {
          input: {
            sourcingId: record.id,
          },
        },
        meta: { gqlMutation: START_FINANCIAL_EVALUATION_MUTATION },
      },
      {
        onSuccess: (data: any) => {
          message.success('Financial evaluation started');
          setIsFinancialInProgress(true);
          setFinancialPhase(EvaluationPhase.FINANCIAL_IN_PROGRESS);
        },
        onError: (error: any) => {
          console.error('Start financial evaluation error:', error);
          message.error('Failed to start financial evaluation');
        },
      },
    );
  };

  const handleCompleteFinancialEvaluation = () => {
    completeFinancialEvaluation(
      {
        url: "",
        method: "post",
        values: {
          input: {
            sourcingId: record.id,
          },
        },
        meta: { gqlMutation: COMPLETE_FINANCIAL_EVALUATION_MUTATION },
      },
      {
        onSuccess: (data: any) => {
          message.success('Financial evaluation completed');
          setIsFinancialCompleted(true);
          setIsFinancialInProgress(false);
          setFinancialPhase(EvaluationPhase.FINANCIAL_COMPLETED);
        },
        onError: (error: any) => {
          console.error('Complete financial evaluation error:', error);
          message.error('Failed to complete financial evaluation');
        },
      },
    );
  };

  const handleStartTechnicalEvaluation = () => {
    startTechnicalEvaluation(
      {
        url: "",
        method: "post",
        values: {
          input: {
            sourcingId: record.id,
          },
        },
        meta: { gqlMutation: START_TECHNICAL_EVALUATION_MUTATION },
      },
      {
        onSuccess: (data: any) => {
          message.success('Technical evaluation started');
          setIsTechnicalInProgress(true);
          setTechnicalPhase(EvaluationPhase.TECHNICAL_IN_PROGRESS);
        },
        onError: (error: any) => {
          console.error('Start technical evaluation error:', error);
          message.error('Failed to start technical evaluation');
        },
      },
    );
  };

  const handleCompleteTechnicalEvaluation = () => {
    completeTechnicalEvaluation(
      {
        url: "",
        method: "post",
        values: {
          input: {
            sourcingId: record.id,
          },
        },
        meta: { gqlMutation: COMPLETE_TECHNICAL_EVALUATION_MUTATION },
      },
      {
        onSuccess: (data: any) => {
          message.success('Technical evaluation completed');
          setIsTechnicalCompleted(true);
          setIsTechnicalInProgress(false);
          setTechnicalPhase(EvaluationPhase.TECHNICAL_COMPLETED);
        },
        onError: (error: any) => {
          console.error('Complete technical evaluation error:', error);
          message.error('Failed to complete technical evaluation');
        },
      },
    );
  };

  const handlePhaseTransition = () => {
    // This will be called when technical evaluation phase changes
    // We need to refresh the component to reflect the new phase
    setTechnicalPhase(record.evaluationPhase);
  };

  // Check if financial evaluation should be accessible
  const canAccessFinancialEvaluation = (
    record.technicalEvaluationMode === TechnicalEvaluationMode.NONE ||
    isTechnicalCompleted
  );

  // Determine which tabs to show based on evaluation mode
  const showTechnicalTab = record.technicalEvaluationMode !== TechnicalEvaluationMode.NONE;

  const renderTechnicalEvaluationTab = () => {
    if (record.technicalEvaluationMode === TechnicalEvaluationMode.NONE) {
      return (
        <Card>
          <Alert
            message="No Technical Evaluation Required"
            description="This sourcing event does not require technical evaluation. Proceed directly to financial evaluation."
            type="info"
            showIcon
          />
        </Card>
      );
    }

    if (record.technicalEvaluationMode === TechnicalEvaluationMode.PASS_FAIL) {
      return (
        <ComplianceTabulation 
          record={record} 
          onPhaseTransition={handlePhaseTransition}
        />
      );
    }

    if (record.technicalEvaluationMode === TechnicalEvaluationMode.MATRIX) {
      return (
        <Space direction="vertical" style={{ width: '100%' }}>
          <EvaluationMatrix record={record} viewType="technical" />
          
          <Card type="inner" style={{ marginTop: '16px' }}>
            <Space>
              {!isTechnicalInProgress && !isTechnicalCompleted && (
                <Button
                  type="primary"
                  onClick={handleStartTechnicalEvaluation}
                  icon={<CheckCircleOutlined />}
                >
                  Start Technical Evaluation
                </Button>
              )}

              {isTechnicalInProgress && !isTechnicalCompleted && (
                <Button
                  type="primary"
                  onClick={handleCompleteTechnicalEvaluation}
                  icon={<CheckCircleOutlined />}
                >
                  Complete Technical Evaluation
                </Button>
              )}

              {isTechnicalCompleted && (
                <Alert
                  message="Technical Evaluation Completed"
                  description="Technical evaluation has been completed. You can now proceed to financial evaluation."
                  type="success"
                  showIcon
                />
              )}
            </Space>
          </Card>
        </Space>
      );
    }

    return (
      <Card>
        <Alert
          message="Unsupported Technical Evaluation Mode"
          description={`Technical evaluation mode ${record.technicalEvaluationMode} is not supported.`}
          type="warning"
          showIcon
        />
      </Card>
    );
  };

  const renderFinancialEvaluationTab = () => {
    // For NONE mode, show all bids in financial evaluation
    if (record.technicalEvaluationMode === TechnicalEvaluationMode.NONE) {
      return (
        <Space direction="vertical" style={{ width: '100%' }}>
         {(isFinancialInProgress || isFinancialCompleted) && <PriceTabulation record={record} isFinancialCompleted={isFinancialCompleted} /> }
          
          {!isFinancialInProgress && !isFinancialCompleted && (
            <Card type="inner">
              <Button
                type="primary"
                onClick={handleStartFinancialEvaluation}
                icon={<CheckCircleOutlined />}
              >
                Start Financial Evaluation
              </Button>
            </Card>
          )}

          {isFinancialInProgress && !isFinancialCompleted && (
            <Card type="inner">
              <Button
                type="primary"
                onClick={handleCompleteFinancialEvaluation}
                icon={<CheckCircleOutlined />}
              >
                Complete Financial Evaluation
              </Button>
            </Card>
          )}

          {isFinancialCompleted && (
            <Card type="inner">
              <Alert
                message="Financial Evaluation Completed"
                description="Financial evaluation has been completed successfully."
                type="success"
                showIcon
              />
            </Card>
          )}
        </Space>
      );
    }

    // For PASS_FAIL and MATRIX modes, show only qualified bids
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
      
        
               {(isFinancialInProgress || isFinancialCompleted) && <Fragment>
                  <Alert
          message="Financial Evaluation"
          description="Only bids that passed technical evaluation are shown here."
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <PriceTabulation record={record} isFinancialCompleted={isFinancialCompleted} />
        <EvaluationMatrix record={record} viewType="financial" isFinancialCompleted={isFinancialCompleted} />
               </Fragment>  }
        
        {!isFinancialInProgress && !isFinancialCompleted && isTechnicalCompleted && (
          <Card type="inner">
            <Button
              type="primary"
              onClick={handleStartFinancialEvaluation}
              icon={<CheckCircleOutlined />}
            >
              Start Financial Evaluation
            </Button>
          </Card>
        )}

        {isFinancialInProgress && !isFinancialCompleted && (
          <Card type="inner">
            <Button
              type="primary"
              onClick={handleCompleteFinancialEvaluation}
              icon={<CheckCircleOutlined />}
            >
              Complete Financial Evaluation
            </Button>
          </Card>
        )}

        {isFinancialCompleted && (
          <Card type="inner">
            <Alert
              message="Financial Evaluation Completed"
              description="Financial evaluation has been completed successfully."
              type="success"
              showIcon
            />
          </Card>
        )}
      </Space>
    );
  };

  const tabItems = [
    {
      key: 'technical',
      label: (
        <Space>
          <Text>Technical Evaluation</Text>
          {record.technicalEvaluationMode === TechnicalEvaluationMode.PASS_FAIL && (
            <Tag color="blue">PASS/FAIL</Tag>
          )}
          {record.technicalEvaluationMode === TechnicalEvaluationMode.MATRIX && (
            <Tag color="purple">MATRIX</Tag>
          )}
          {isTechnicalInProgress && <Tag color="processing">IN PROGRESS</Tag>}
          {isTechnicalCompleted && <Tag color="success">COMPLETED</Tag>}
        </Space>
      ),
      children: renderTechnicalEvaluationTab(),
      disabled: !showTechnicalTab,
    },
    {
      key: 'financial',
      label: (
        <Space>
          <Text>Financial Evaluation</Text>
          {isFinancialInProgress && <Tag color="processing">IN PROGRESS</Tag>}
          {isFinancialCompleted && <Tag color="success">COMPLETED</Tag>}
          {!canAccessFinancialEvaluation && !isFinancialCompleted && (
            <Tag color="warning">LOCKED</Tag>
          )}
        </Space>
      ),
      children: renderFinancialEvaluationTab(),
      disabled: !canAccessFinancialEvaluation && !isFinancialCompleted,
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {/* Phase Indicator */}
      <Card size="small">
        <Space>
          <Text strong>Evaluation Phase:</Text>
          <Tag color={
            technicalPhase === EvaluationPhase.NOT_STARTED ? 'default' :
            technicalPhase === EvaluationPhase.TECHNICAL_IN_PROGRESS ? 'processing' :
            technicalPhase === EvaluationPhase.TECHNICAL_COMPLETED ? 'success' :
            financialPhase === EvaluationPhase.FINANCIAL_IN_PROGRESS ? 'processing' :
            financialPhase === EvaluationPhase.FINANCIAL_COMPLETED ? 'success' :
            'success'
          }>
            {technicalPhase?.replace(/_/g, ' ') || 'NOT STARTED'}
          </Tag>
          
          {record.technicalEvaluationMode !== TechnicalEvaluationMode.NONE && activeTab === 'financial' && (
            <Text type="secondary">
              {isTechnicalCompleted
                ? 'Financial evaluation enabled'
                : 'Complete technical evaluation to enable financial evaluation'}
            </Text>
          )}
        </Space>
      </Card>

      {/* Main Tabs */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          type="card"
        />
      </Card>

      {/* Overall Evaluation Status - Only show when financial evaluation is completed */}
      {isFinancialCompleted && (
        <Card title="Evaluation Progress" size="small">
          <Space direction="vertical">
            <div>
              <Text strong>Technical Evaluation:</Text>
              {showTechnicalTab ? (
                <>
                  {isTechnicalInProgress && <Tag color="processing">IN PROGRESS</Tag>}
                  {isTechnicalCompleted && <Tag color="success">COMPLETED</Tag>}
                  {!isTechnicalInProgress && !isTechnicalCompleted && <Tag color="default">NOT STARTED</Tag>}
                </>
              ) : (
                <Tag color="default">NOT REQUIRED</Tag>
              )}
            </div>
           
            <div>
              <Text strong>Financial Evaluation:</Text>
              {isFinancialInProgress && <Tag color="processing">IN PROGRESS</Tag>}
              {isFinancialCompleted && <Tag color="success">COMPLETED</Tag>}
              {!isFinancialInProgress && !isFinancialCompleted && <Tag color="default">NOT STARTED</Tag>}
              {!canAccessFinancialEvaluation && !isFinancialCompleted && (
                <Text type="secondary"> (Locked until technical evaluation completes)</Text>
              )}
            </div>
          </Space>
        </Card>
      )}
    </Space>
  );
};